import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { auth, db, firebaseEnv } from "../firebase";
import PageHeader from "../components/PageHeader";

type SurveyStatus = "published" | "unpublished" | "deleted";
type ListStatus = "active" | "deleted";

type SurveyOption = {
  id: string;
  title: string;
  status: SurveyStatus;
  ownerUid: string | null;
};

type ListOption = {
  id: string;
  name: string;
  status: ListStatus;
  ownerUid: string | null;
};

type TestDataConfig = {
  enabled: boolean;
  surveyTemplateIds: string[];
  listTemplateIds: string[];
};

const CONFIG_DOC_ID = "testData";

function normalizeSurveyStatus(data: any): SurveyStatus {
  if (data?.deleted === true || data?.isDeleted === true) return "deleted";
  if (data?.status === "published" || data?.status === "unpublished" || data?.status === "deleted") {
    return data.status;
  }
  return data?.published === true ? "published" : "unpublished";
}

export default function AdminTestData() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  const [allSurveys, setAllSurveys] = useState<SurveyOption[]>([]);
  const [allLists, setAllLists] = useState<ListOption[]>([]);

  const [enabled, setEnabled] = useState(false);
  const [selectedSurveyIds, setSelectedSurveyIds] = useState<string[]>([]);
  const [selectedListIds, setSelectedListIds] = useState<string[]>([]);

  const [pickedSurveyIds, setPickedSurveyIds] = useState<Set<string>>(new Set());
  const [pickedListIds, setPickedListIds] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setErrMsg("You need to sign in to manage test data.");
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrMsg(null);
    setSaveMsg(null);

    try {
      const meSnap = await getDoc(doc(db, "users", currentUser.uid));
      const myRole = meSnap.exists() ? (meSnap.data() as any)?.accountRole : undefined;
      const admin = myRole === "admin";
      setIsAdmin(admin);

      if (!admin) {
        setErrMsg("Admin access is required.");
        setLoading(false);
        return;
      }

      const [configSnap, surveysSnap, listsSnap] = await Promise.all([
        getDoc(doc(db, "adminSettings", CONFIG_DOC_ID)),
        getDocs(collection(db, "surveys")),
        getDocs(collection(db, "lists")),
      ]);

      const config = configSnap.exists() ? (configSnap.data() as Partial<TestDataConfig>) : {};
      setEnabled(config.enabled === true);

      const surveys: SurveyOption[] = surveysSnap.docs
        .map((d) => {
          const data = d.data() as any;
          const ownerUid = data?.ownerId ?? data?.userId ?? null;
          return {
            id: d.id,
            title: data?.title || "(Untitled)",
            status: normalizeSurveyStatus(data),
            ownerUid,
          };
        })
        .filter((s) => s.ownerUid === currentUser.uid)
        .sort((a, b) => a.title.localeCompare(b.title));

      const configuredSurveyIds = Array.isArray(config.surveyTemplateIds)
        ? config.surveyTemplateIds.filter((id): id is string => typeof id === "string")
        : [];
      const allowedSurveyIds = new Set(
        surveys.filter((s) => s.status !== "deleted").map((s) => s.id)
      );
      setSelectedSurveyIds(configuredSurveyIds.filter((id) => allowedSurveyIds.has(id)));

      const lists: ListOption[] = listsSnap.docs
        .map((d) => {
          const data = d.data() as any;
          const status: ListStatus = data?.status === "deleted" ? "deleted" : "active";
          const ownerUid = data?.ownerId ?? data?.userId ?? null;
          return {
            id: d.id,
            name: data?.name || "(Untitled)",
            status,
            ownerUid,
          };
        })
        .filter((l) => l.ownerUid === currentUser.uid)
        .sort((a, b) => a.name.localeCompare(b.name));

      const configuredListIds = Array.isArray(config.listTemplateIds)
        ? config.listTemplateIds.filter((id): id is string => typeof id === "string")
        : [];
      const allowedListIds = new Set(
        lists.filter((l) => l.status !== "deleted").map((l) => l.id)
      );
      setSelectedListIds(configuredListIds.filter((id) => allowedListIds.has(id)));
      setPickedSurveyIds(new Set());
      setPickedListIds(new Set());

      setAllSurveys(surveys);
      setAllLists(lists);
    } catch (err: any) {
      setErrMsg(err?.message || "Failed to load test data settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const availableSurveys = useMemo(
    () => allSurveys.filter((s) => !selectedSurveyIds.includes(s.id) && s.status !== "deleted"),
    [allSurveys, selectedSurveyIds]
  );
  const chosenSurveys = useMemo(
    () => selectedSurveyIds
      .map((id) => allSurveys.find((s) => s.id === id))
      .filter((s): s is SurveyOption => !!s && s.status !== "deleted"),
    [allSurveys, selectedSurveyIds]
  );

  const availableLists = useMemo(
    () => allLists.filter((l) => !selectedListIds.includes(l.id) && l.status !== "deleted"),
    [allLists, selectedListIds]
  );
  const chosenLists = useMemo(
    () => selectedListIds
      .map((id) => allLists.find((l) => l.id === id))
      .filter((l): l is ListOption => !!l && l.status !== "deleted"),
    [allLists, selectedListIds]
  );

  const moveSurveysRight = () => {
    if (!pickedSurveyIds.size) return;
    setSelectedSurveyIds((prev) => [...prev, ...Array.from(pickedSurveyIds)]);
    setPickedSurveyIds(new Set());
  };
  const moveSurveysLeft = () => {
    if (!pickedSurveyIds.size) return;
    setSelectedSurveyIds((prev) => prev.filter((id) => !pickedSurveyIds.has(id)));
    setPickedSurveyIds(new Set());
  };
  const moveListsRight = () => {
    if (!pickedListIds.size) return;
    setSelectedListIds((prev) => [...prev, ...Array.from(pickedListIds)]);
    setPickedListIds(new Set());
  };
  const moveListsLeft = () => {
    if (!pickedListIds.size) return;
    setSelectedListIds((prev) => prev.filter((id) => !pickedListIds.has(id)));
    setPickedListIds(new Set());
  };

  const save = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    setSaving(true);
    setErrMsg(null);
    setSaveMsg(null);

    try {
      await setDoc(
        doc(db, "adminSettings", CONFIG_DOC_ID),
        {
          enabled,
          surveyTemplateIds: selectedSurveyIds,
          listTemplateIds: selectedListIds,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.uid,
        },
        { merge: true }
      );
      setSaveMsg("Test data settings saved.");
    } catch (err: any) {
      setErrMsg(err?.message || "Failed to save test data settings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Loading test data settings…</Typography>
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{errMsg || "Admin access is required."}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <PageHeader
        title="Manage Test Data"
        subtitle={`Admin-only test data controls (${firebaseEnv} environment)`}
        actions={<Button variant="outlined" onClick={load}>Refresh</Button>}
      />

      <Stack spacing={2}>
        <Alert severity="info">
          Turn this on to show a “Populate Test Data” button on the surveys page for all signed-in users.
        </Alert>

        {errMsg && <Alert severity="error">{errMsg}</Alert>}
        {saveMsg && <Alert severity="success">{saveMsg}</Alert>}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <FormControlLabel
            control={<Switch checked={enabled} onChange={(_, checked) => setEnabled(checked)} />}
            label={enabled ? "Populate button enabled" : "Populate button disabled"}
          />
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Survey Templates</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Move surveys to the right to make them available for copying.
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 2 }}>
            <Paper variant="outlined" sx={{ p: 1, minHeight: 240 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Available ({availableSurveys.length})
              </Typography>
              <Stack spacing={0.5}>
                {availableSurveys.map((s) => (
                  <FormControlLabel
                    key={s.id}
                    control={
                      <Checkbox
                        checked={pickedSurveyIds.has(s.id)}
                        onChange={(_, checked) =>
                          setPickedSurveyIds((prev) => {
                            const next = new Set(prev);
                            if (checked) next.add(s.id);
                            else next.delete(s.id);
                            return next;
                          })
                        }
                      />
                    }
                    label={`${s.title} (${s.id})`}
                  />
                ))}
              </Stack>
            </Paper>
            <Stack justifyContent="center" spacing={1}>
              <Button variant="outlined" onClick={moveSurveysRight}>{">"}</Button>
              <Button variant="outlined" onClick={moveSurveysLeft}>{"<"}</Button>
            </Stack>
            <Paper variant="outlined" sx={{ p: 1, minHeight: 240 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Enabled For Copy ({chosenSurveys.length})
              </Typography>
              <Stack spacing={0.5}>
                {chosenSurveys.map((s) => (
                  <FormControlLabel
                    key={s.id}
                    control={
                      <Checkbox
                        checked={pickedSurveyIds.has(s.id)}
                        onChange={(_, checked) =>
                          setPickedSurveyIds((prev) => {
                            const next = new Set(prev);
                            if (checked) next.add(s.id);
                            else next.delete(s.id);
                            return next;
                          })
                        }
                      />
                    }
                    label={`${s.title} (${s.id})`}
                  />
                ))}
              </Stack>
            </Paper>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>List Templates</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Move lists to the right to make them available for copying.
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 2 }}>
            <Paper variant="outlined" sx={{ p: 1, minHeight: 240 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Available ({availableLists.length})
              </Typography>
              <Stack spacing={0.5}>
                {availableLists.map((l) => (
                  <FormControlLabel
                    key={l.id}
                    control={
                      <Checkbox
                        checked={pickedListIds.has(l.id)}
                        onChange={(_, checked) =>
                          setPickedListIds((prev) => {
                            const next = new Set(prev);
                            if (checked) next.add(l.id);
                            else next.delete(l.id);
                            return next;
                          })
                        }
                      />
                    }
                    label={`${l.name} (${l.id})`}
                  />
                ))}
              </Stack>
            </Paper>
            <Stack justifyContent="center" spacing={1}>
              <Button variant="outlined" onClick={moveListsRight}>{">"}</Button>
              <Button variant="outlined" onClick={moveListsLeft}>{"<"}</Button>
            </Stack>
            <Paper variant="outlined" sx={{ p: 1, minHeight: 240 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Enabled For Copy ({chosenLists.length})
              </Typography>
              <Stack spacing={0.5}>
                {chosenLists.map((l) => (
                  <FormControlLabel
                    key={l.id}
                    control={
                      <Checkbox
                        checked={pickedListIds.has(l.id)}
                        onChange={(_, checked) =>
                          setPickedListIds((prev) => {
                            const next = new Set(prev);
                            if (checked) next.add(l.id);
                            else next.delete(l.id);
                            return next;
                          })
                        }
                      />
                    }
                    label={`${l.name} (${l.id})`}
                  />
                ))}
              </Stack>
            </Paper>
          </Box>
        </Paper>

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
